import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";

const COOKIE_CONSENT_KEY = "cookie_consent";
const COOKIE_CONSENT_VERSION = "2";

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  version: string;
  timestamp: number;
}

const CookieConsent = () => {
  const { t } = useTranslations();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    version: COOKIE_CONSENT_VERSION,
    timestamp: Date.now(),
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ConsentPreferences;
        if (parsed.version !== COOKIE_CONSENT_VERSION) {
          setVisible(true);
        }
      } catch {
        setVisible(true);
      }
    } else {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (prefs: ConsentPreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setVisible(false);
  };

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      version: COOKIE_CONSENT_VERSION,
      timestamp: Date.now(),
    });
  };

  const handleRejectAll = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      version: COOKIE_CONSENT_VERSION,
      timestamp: Date.now(),
    });
  };

  const handleSavePreferences = () => {
    savePreferences({
      ...preferences,
      version: COOKIE_CONSENT_VERSION,
      timestamp: Date.now(),
    });
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 animate-fade-in">
      <div className="container">
        <div className="bg-card border border-border rounded-lg shadow-lg p-4 md:p-6 max-w-4xl mx-auto">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <h3 className="font-display text-lg md:text-xl mb-2">{t("cookie.title")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("cookie.desc")}{" "}
                <Link to="/privacy" className="underline hover:text-foreground transition-smooth">
                  {t("legal.privacy_policy")}
                </Link>
              </p>
            </div>
            <button
              onClick={handleRejectAll}
              className="text-muted-foreground hover:text-foreground transition-smooth"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {showDetails && (
            <div className="mb-4 space-y-3 border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t("cookie.necessary")}</p>
                  <p className="text-xs text-muted-foreground">{t("cookie.necessary_desc")}</p>
                </div>
                <span className="text-xs text-muted-foreground">{t("cookie.always_active")}</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t("cookie.analytics")}</p>
                  <p className="text-xs text-muted-foreground">{t("cookie.analytics_desc")}</p>
                </div>
                <button
                  onClick={() => setPreferences((p) => ({ ...p, analytics: !p.analytics }))}
                  className={`w-10 h-5 rounded-full transition-colors ${
                    preferences.analytics ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-background transition-transform ${
                      preferences.analytics ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="order-3 sm:order-1"
            >
              {showDetails ? t("cookie.hide_details") : t("cookie.customize")}
            </Button>
            {showDetails ? (
              <Button size="sm" onClick={handleSavePreferences} className="order-1 sm:order-2">
                {t("cookie.save_preferences")}
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={handleRejectAll} className="order-2">
                  {t("cookie.reject_all")}
                </Button>
                <Button size="sm" onClick={handleAcceptAll} className="order-1 sm:order-3">
                  {t("cookie.accept_all")}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
